import { useEffect, useState } from 'react'

import type { ChangeEvent } from '../utils'
import type { CheckboxProps, CheckboxStyleProps } from './checkbox.types'

import Box from '../box'
import { omitThemingProps, styled, th, useStyleConfig, vui } from '../core'
import Icon from '../icon'
import {
  SelectionControlInput,
  useDeprecatedLgSize,
  useSelectionControlColors,
  useSelectionControlDescription,
} from '../selectionControl/selectionControl'
import T from '../t'
import { cs, filterUndefined } from '../utils'
import { checkboxColors, disabled as disabledColors } from './consts'
import { useCheckboxGroupContext } from './context'

// Figma "VUI v5 (modernized)" Checkbox spec (node 2098:2155/2098:2164): gap between box and label scales with size.
const controlGap: Record<string, string> = { lg: '10px', md: '10px', sm: '8px', xs: '6px' }
// Offset decreases as control size grows, so JSX children stay visually centered with the first text line.
const jsxChildrenMt: Record<string, string> = { lg: '0px', md: '0px', sm: '2px', xs: '3px' }
// Centers the box on the label's first line: (caption line-height 150% of font-size - control box height) / 2.
const labelMt: Record<string, string> = { lg: '0px', md: '0px', sm: '0.5px', xs: '1px' }
// Inner mark (checkmark/minus) size + inset from the box edge, per size (node 2098:2155/2439:576).
const markSize: Record<string, string> = { lg: '16px', md: '16px', sm: '13px', xs: '10px' }
const markOffset: Record<string, string> = { lg: '3px', md: '3px', sm: '2.5px', xs: '2px' }

const CheckboxControl = styled.spanBox`
  border: 1px solid currentColor;
  border-radius: 0;
  display: inline-flex;
  flex-shrink: 0;
  position: relative;
  transition-duration: fast;
`

export const CheckboxBase = styled.labelBox<CheckboxStyleProps>`
  cursor: pointer;
  display: inline-flex;
  width: fit-content;

  &:not([aria-disabled='true']):not([aria-readonly='true']):hover .vui-checkboxControl {
    color: ${p => th.color(p.controlHoverColor)};
  }

  &:not([aria-disabled='true']):not([aria-readonly='true']):active .vui-checkboxControl {
    color: ${p => th.color(p.controlPressedColor)};
  }

  &[aria-readonly='true'] {
    cursor: auto;
  }

  &[aria-disabled='true'] {
    color: ${disabledColors.text};
    cursor: not-allowed;

    .vui-checkboxControl {
      background-color: ${disabledColors.bg};
      color: ${disabledColors.icon};
    }
  }
`

/**
 * Allows selection of one or more choices from a set of items. Handles controlled and uncontrolled modes.
 * The box itself is plain CSS (border/background); only the checkmark/minus mark is an icon. Can be
 * indeterminate when selecting some nested items.
 */
export const Checkbox = vui<'span', CheckboxProps>((props, ref) => {
  const { defaultValue: groupDefaultValue, ...checkboxGroupProps } = useCheckboxGroupContext() ?? {}

  const mergedProps = { ...checkboxGroupProps, ...props }
  const {
    checked,
    children,
    className,
    defaultChecked = groupDefaultValue !== undefined ? groupDefaultValue.includes(props.value) : undefined,
    disabled,
    errorText,
    readOnly,
    helpText,
    icon: iconProp,
    iconChecked = 'uiCheck',
    iconIndeterminate = 'uiMinus',
    id: idProp,
    inputProps,
    inputRef,
    isIndeterminate,
    label,
    name,
    onChange: onChangeProp,
    required,
    value,
    ...rest
  } = omitThemingProps(mergedProps)
  const [isChecked, setIsChecked] = useState(defaultChecked)
  const styles = useStyleConfig('Checkbox', mergedProps)
  const { color: controlColor, hoverColor, pressedColor, ...controlStyles } = styles.control
  const size = mergedProps.size ?? 'md'
  const isCheckedOrIndeterminate = isChecked || isIndeterminate
  const {
    description,
    descriptionId,
    errorText: effectiveErrorText,
    id,
  } = useSelectionControlDescription(idProp, helpText, errorText, isCheckedOrIndeterminate)

  const markIcon = isIndeterminate ? iconIndeterminate : isChecked ? iconChecked : iconProp
  const controlMr = children || label ? (controlGap[size] ?? controlGap.md) : 0
  const { color, controlHoverColor, controlPressedColor, descriptionColor, labelColor } = useSelectionControlColors({
    controlColor,
    differentiateUnchecked: true,
    disabled,
    errorText: effectiveErrorText,
    hoverColor,
    isActive: isCheckedOrIndeterminate,
    pressedColor,
    readOnly,
  })
  // White/inverse mark on the filled checked/indeterminate box; the current border colour otherwise (custom `icon` overlay).
  const markColor = disabled ? disabledColors.mark : isCheckedOrIndeterminate ? checkboxColors.mark : color
  const hasJsxChildren = !!children

  useDeprecatedLgSize(size, 'Checkbox')

  useEffect(() => {
    checked !== undefined && setIsChecked(checked)
  }, [checked])

  const onChange = (e: ChangeEvent) => {
    if (readOnly) return
    if (checked === undefined) setIsChecked(e.target.checked)
    onChangeProp?.(e)
  }

  const aliasedProps = filterUndefined({
    'aria-disabled': disabled,
    'aria-readonly': readOnly,
  })

  return (
    <CheckboxBase
      alignItems={label || hasJsxChildren ? 'flex-start' : 'center'}
      className={cs('vui-checkbox', className)}
      controlHoverColor={controlHoverColor}
      controlPressedColor={controlPressedColor}
      ref={ref}
      {...styles.container}
      {...aliasedProps}
      {...rest}
    >
      <CheckboxControl
        bg={isCheckedOrIndeterminate ? 'currentColor' : 'white'}
        className="vui-checkboxControl"
        color={color}
        focusWithinRing={3}
        focusWithinRingColor={checkboxColors.focus}
        mr={controlMr}
        mt={
          hasJsxChildren ? (jsxChildrenMt[size] ?? jsxChildrenMt.md) : label ? (labelMt[size] ?? labelMt.md) : undefined
        }
        role="none"
        {...controlStyles}
      >
        <SelectionControlInput
          aria-describedby={descriptionId}
          aria-invalid={!!effectiveErrorText || undefined}
          className="vui-checkboxInput"
          onChange={onChange}
          ref={inputRef}
          type="checkbox"
          {...{ checked, defaultChecked, disabled, id, name, required, value }}
          {...inputProps}
        />
        {markIcon && (
          <Icon
            className="vui-checkboxIcon"
            color={markColor}
            h={markSize[size] ?? markSize.md}
            left={markOffset[size] ?? markOffset.md}
            name={markIcon}
            position="absolute"
            top={markOffset[size] ?? markOffset.md}
            w={markSize[size] ?? markSize.md}
          />
        )}
      </CheckboxControl>

      {children ??
        (label && (
          <Box column>
            <T className="vui-checkboxLabel" color={labelColor} variant="caption" {...styles.label}>
              {label}
            </T>
            {description && (
              <T className="vui-checkboxDescription" color={descriptionColor} id={descriptionId} mt="2px" size="sm">
                {description}
              </T>
            )}
          </Box>
        ))}
    </CheckboxBase>
  )
})

Checkbox.displayName = 'Checkbox'
export default Checkbox
